import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AlertTriangle, Activity, RefreshCw, Loader2, HeartPulse,
  Building2, Users, Timer, ShieldAlert, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_PILL = {
  very_high: 'bg-red-50 text-red-700 border-red-200',
  high: 'bg-amber-50 text-amber-700 border-amber-200',
  moderate: 'bg-blue-50 text-blue-700 border-blue-200',
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Building2, label: 'Referral Sources', href: '/operations/referral-sources' },
  { icon: Timer, label: 'Referral SLA', href: '/admin/referral-sla' },
  { icon: HeartPulse, label: 'Readmission Risk', active: true },
  { icon: ShieldAlert, label: 'EVV Compliance', href: '/admin/evv' },
  { icon: Users, label: 'Clinician Retention', href: '/admin/clinician-retention' },
];

export default function AdminReadmissionRisk() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/readmission-risk`, getAuthHeader());
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load readmission cohort');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis || {};

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Readmission Risk Cohort"
      subtitle="LACE+ scored list of patients discharged in the last 30 days. Intervene on very-high-risk patients before they bounce back and CMS penalties apply."
      testId="admin-readmission-risk-page"
    >
      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}

      {!loading && data && (
        <>
          <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3">
            <Button onClick={load} className="cn-btn cn-btn-ghost !py-2" data-testid="readmission-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
            <span className="text-xs text-cn-text-muted ml-auto">
              30-day window · {kpis.cohort_size || 0} patients scored
            </span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="readmission-kpis">
            <KpiTile label="Cohort size" value={kpis.cohort_size || 0} icon={Users} tone="neutral" testid="readmission-kpi-total" />
            <KpiTile label="Very high" value={kpis.very_high || 0} icon={AlertTriangle} tone="red" testid="readmission-kpi-very-high" />
            <KpiTile label="High" value={kpis.high || 0} icon={TrendingDown} tone="amber" testid="readmission-kpi-high" />
            <KpiTile label="Moderate" value={kpis.moderate || 0} icon={Activity} tone="blue" testid="readmission-kpi-moderate" />
            <KpiTile
              label="Expected readmits (30d)"
              value={kpis.expected_readmits_30d || 0}
              icon={HeartPulse}
              tone="red"
              sub="Σ probability across cohort"
              testid="readmission-kpi-expected"
            />
          </div>

          <div className="cn-card overflow-hidden" data-testid="readmission-cohort-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base text-cn-text">Cohort ranked by risk</h4>
              <p className="text-xs text-cn-text-muted mt-0.5">Sorted very-high → low. Click &apos;Interventions&apos; to see protocol recommendations.</p>
            </div>
            {(data.cohort || []).length === 0 ? (
              <div className="p-10 text-center" data-testid="readmission-empty">
                <p className="text-sm text-cn-text-muted">No patients discharged in the last 30 days.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="readmission-cohort-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">Patient</th>
                      <th className="px-3 py-3 text-right">Disch.</th>
                      <th className="px-3 py-3 text-right">LOS</th>
                      <th className="px-3 py-3 text-right">ED visits (6mo)</th>
                      <th className="px-3 py-3 text-right">LACE</th>
                      <th className="px-3 py-3 text-right">P(readmit)</th>
                      <th className="px-3 py-3">Band</th>
                      <th className="px-3 py-3">Interventions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {(data.cohort || []).slice(0, 100).map((c) => (
                      <tr key={c.patient_id} className="hover:bg-cn-bone-50/40 align-top"
                        data-testid={`readmission-row-${c.patient_id}`}>
                        <td className="px-5 py-2.5 font-medium">{c.patient_name}</td>
                        <td className="px-3 py-2.5 text-right text-xs text-cn-text-muted">
                          {c.days_since_discharge}d ago
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs">
                          {c.length_of_stay_days !== null ? `${c.length_of_stay_days}d` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs">{c.ed_visits_prior_180d}</td>
                        <td className="px-3 py-2.5 text-right font-semibold">{c.lace_total}</td>
                        <td className="px-3 py-2.5 text-right text-xs font-semibold text-red-700">{c.readmission_probability_pct}%</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${BAND_PILL[c.risk_band]}`}>
                            {c.risk_band.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-cn-text-muted max-w-md">
                          <ul className="list-disc pl-4 space-y-0.5">
                            {(c.recommended_interventions || []).slice(0, 3).map((i, idx) => (
                              <li key={idx}>{i}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </DashboardShell>
  );
}

function KpiTile({ label, value, icon: Icon, tone = 'neutral', testid, sub }) {
  const iconCls = {
    neutral: 'text-cn-primary', amber: 'text-amber-600', blue: 'text-blue-600',
    emerald: 'text-emerald-600', red: 'text-red-600',
  }[tone];
  return (
    <div className="cn-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <Icon className={`w-4 h-4 ${iconCls}`} />
      </div>
      <p className="font-display font-bold text-2xl text-cn-text dark:text-white">{value}</p>
      {sub && <p className="text-[10px] text-cn-text-muted mt-0.5">{sub}</p>}
    </div>
  );
}
